<template>
  <div>
    <BlockUI v-if="loading" message="Loading">
      <i class="fa fa-spinner fa-spin fa-3x fa-fw"></i>
    </BlockUI>
    <v-card>
      <v-toolbar dense class="elevation-0">
        <v-toolbar-title>{{ instance.code }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outline :color="$options.filters.statusBgColor(instance.status)">{{ instance.status }}</v-chip>
        <v-btn flat icon color="warning" :href="xlsUrl" target="_blank" v-if="!isCanceled">
          <v-icon>mdi-microsoft-excel</v-icon>
        </v-btn>
        <v-btn flat icon color="red" :href="printoutUrl" target="_blank" v-if="!isCanceled">
          <v-icon>mdi-adobe-acrobat</v-icon>
        </v-btn>
        <v-btn flat icon color="primary" @click="print" v-if="!isCanceled">
          <v-icon>print</v-icon>
        </v-btn>
        <v-btn flat icon color="warning" @click="locked = !locked" :disabled="isCompleted" v-if="!isCanceled">
          <v-icon>{{ (locked) ? 'lock' : 'lock_open' }}</v-icon>
        </v-btn>
        <v-btn flat icon @click="gotoList">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-form v-model="valid" ref="qform" lazy-validation>
        <v-container grid-list-md fluid>
          <v-layout row wrap>
            <v-flex xs12 md12>
              <v-text-field
                required
                :rules="[v => !!v || 'Name is required']"
                label="Name"
                v-model="instance.name"
                :disabled="!isEditable"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md12>
              <v-select return-object required :rules="[v => !!v || 'Customer is required']"
                :items="customerItems"
                v-model="instance.customer"
                label="Customer"
                item-value="id"
                item-text="name"
                :disabled="!isEditable"
              ></v-select>
            </v-flex>
            <v-flex xs12 md12>
              <n-chip-date-picker
                v-model="instance.bookDate"
                :error="!instance.bookDate"
                :required="true"
                :is-disabled="!isEditable"
                label="Date"></n-chip-date-picker>
            </v-flex>
            <v-flex xs12 md12>
              <label>Remark</label>
              <ckeditor :editor="editor" v-model="instance.remark" :disabled="!isEditable"></ckeditor>
            </v-flex>
            <v-flex xs12 md12>
              <v-card>
                <v-card-title class="orange lighten-5">Payment</v-card-title>
                <v-card-text>
                  <v-radio-group v-model="instance.payment.methodOfPayment" row :disabled="!isEditable">
                    <v-radio label="Cash" value="CASH" ></v-radio>
                    <v-radio label="Cheque" value="CHEQUE"></v-radio>
                  </v-radio-group>
                  <v-card v-if="instance.payment.methodOfPayment === 'CHEQUE'" class="grey lighten-5">
                    <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12 md6>
                            <v-text-field
                              v-model="instance.payment.bank"
                              name="payment.bank"
                              label="Bank"
                              id="bank"
                              :disabled="!isEditable"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 md6>
                            <v-text-field
                              v-model="instance.payment.branch"
                              name="payment.branch"
                              label="Branch"
                              id="branch"
                              :disabled="!isEditable"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 md6>
                            <v-text-field
                              v-model="instance.payment.chequeNo"
                              name="payment.chequeNo"
                              label="Cheque No."
                              :disabled="!isEditable"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 md6>
                            <n-chip-date-picker v-model="instance.payment.chequeDate" label="Date" :is-disabled="!isEditable"></n-chip-date-picker>
                          </v-flex>
                        </v-layout>
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-flex>
            <v-flex xs12 md12>
              <items-data-table v-model="instance.items" :is-disabled="!isEditable" :vat-included="true"></items-data-table>
            </v-flex>
          </v-layout>
        </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pl-3 pr-3">
        <v-btn dark class="grey darken-2" @click.native="gotoList">Close</v-btn>
        <v-spacer></v-spacer>
        <v-btn outline color="primary" @click.native="complete" :disabled="!valid" v-if="isDraft && locked" :loading="loading">Complete</v-btn>
        <v-btn outline color="red" @click.native="remove" :disabled="!valid" v-if="isDraft && locked" :loading="loading">Delete</v-btn>
        <v-btn outline color="success" @click.native="save" :disabled="!valid" v-if="isDraft && !locked" :loading="loading">Save</v-btn>
        <v-btn outline color="warning" @click.native="cancel" :disabled="!valid" v-if="isCompleted && locked" :loading="loading">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import swal from 'sweetalert2'
import ItemsDataTable from '@/components/item/ItemList'
import NChipDatePicker from '@/components/date-picker/NChipDatePicker'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

export default {
  name: 'ReceiptShow',
  moduleName: 'Receipt',
  props: ['id'],
  components: { VueEditor, NChipDatePicker, ItemsDataTable },
  data () {
    return {
      loading: false,
      valid: false,
      locked: true,
      editor: ClassicEditor,
      customerItems: [],
      instance: {
        payment: {},
        items: []
      }
    }
  },
  computed: {
    xlsUrl () {
      return this.$store.getters['receipt/xlsxUrl'](this.instance.id)
    },
    printoutUrl () {
      return this.$store.getters['receipt/pdfUrl'](this.instance.id)
    },
    isEditable() {
      return this.isDraft && !this.locked
    },
    isCanceled() {
      return (this.instance.status) && (this.instance.status === 'CANCELED')
    },
    isDraft() {
      return (this.instance.status) && (this.instance.status === 'DRAFT')
    },
    isCompleted() {
      return (this.instance.status) && (this.instance.status === 'COMPLETED')
    }
  },
  methods: {
    reload: _.debounce((self) => {
      self.loading = true
      self.$store.dispatch('receipt/fetchItem', self.id)
        .then((response) => {
          self.instance = response.data
          self.loading = false
        }).catch((error)=>{
          self.loading = false
          swal('Error!', error.message, 'error')
        })
    }, 1000),
    gotoList () {
      this.$router.push('/receipts/list')
    },
    print () {
      this.$store.dispatch('printPdf', this.printoutUrl)
    },
    save() {
      if (this.$refs.qform.validate() && this.instance.bookDate) {
        swal({
          title: `Save ${this.$options.moduleName}!`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          cancelButtonText: 'Close',
          confirmButtonText: 'Confirm'
        }).then(result => {
          if (result.value) {
            this.loading = true
            this.$store.dispatch('receipt/update', this.instance)
            .then((response) => {
              this.loading = false
              this.reload(this)
              this.$snotify.success(`Save ${this.$options.moduleName}.`, 'Success!')
            }).catch(error => {
              this.loading = false
              this.$snotify.error(`${error.response.data.error}:${error.response.data.message}`, 'Fail!')
            })
          }
        })
      }
    },
    complete () {
      if (this.$refs.qform.validate() && this.instance.bookDate && this.isDraft) {
        swal({
          title: `Complete ${this.$options.moduleName}!`,
          text: 'Please, confirm your action. This item cannot be modified afterward!!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          cancelButtonText: 'Close',
          confirmButtonText: 'Confirm'
        }).then(result => {
          if (result.value) {
            this.loading = true
            this.$store.dispatch('receipt/complete', this.instance)
            .then(response => {
              this.loading = false
              this.reload(this)
              this.$snotify.success(`Complete ${this.$options.moduleName}.`, 'Success!')
            }).catch(error => {
              this.loading = false
              this.$snotify.error(`${error.response.data.error}:${error.response.data.message}`, 'Fail!')
            })
          }
        })
      }
    },
    cancel () {
      if (this.isCompleted) {
        swal({
          title: `Cancel ${this.$options.moduleName}!`,
          text: 'Please, confirm your action. This item cannot be modified afterward!!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          cancelButtonText: 'Close',
          confirmButtonText: 'Confirm'
        }).then(result => {
          if (result.value) {
            this.loading = true
            this.$store.dispatch('receipt/cancel', this.instance)
            .then(response => {
              this.loading = false
              this.reload(this)
              this.$snotify.success(`Cancel ${this.$options.moduleName}.`, 'Success!')
            }).catch(error => {
              this.loading = false
              this.$snotify.error(`${error.response.data.error}:${error.response.data.message}`, 'Fail!')
            })
          }
        })
      } else {
        this.$snotify.error('Cancelation is not allowed.', 'Fail!')
      }
    },
    remove () {
      if (this.isDraft) {
        swal({
          title: `Delete ${this.$options.moduleName}!`,
          text: 'Please, confirm your action. This item cannot be modified afterward!!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          cancelButtonText: 'Close',
          confirmButtonText: 'Confirm'
        }).then(result => {
          if (result.value) {
            this.loading = true
            this.$store.dispatch('receipt/delete', this.instance)
            .then(response => {
              this.loading = false
              this.$snotify.success(`Delete ${this.$options.moduleName}.`, 'Success!')
              this.$router.push('/receipts/list')
            }).catch(error => {
              this.loading = false
              this.$snotify.error(`${error.response.data.error}:${error.response.data.message}`, 'Fail!')
            })
          }
        })
      }
    }
  },
  mounted () {
    this.$store.dispatch('customer/loadCustomerItems')
      .then((response) => {
        this.customerItems = response.data
      })
    this.reload(this)
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/views/receipts/show.vue